import {
  useState,
} from 'react';
import classNames from 'classnames';
import {
  reload,
} from 'vike/client/router';
import {
  useUsers,
} from '@dinosave/core/stores';
import type {
  UserEntity,
} from '@dinosave/core/types';
import posthog from '@/analytics/posthog';
import GoogleButton from '@/components/Auth/GoogleButton';
import SignIn from '@/components/Auth/SignIn';
import SignUp from '@/components/Auth/SignUp';
import {
  AUTH,
} from '@/constants/namespaces';
import extension from '@/utils/extension';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';

import styles from './AuthenticationForm.module.css';

type AuthenticatedResponse = {
  user: UserEntity;
  token: string;
}

type AuthenticationFormProps = {
  kind?: 'default' | 'flat';
  className?: string;
  onSuccess?: (user: UserEntity) => void;
}

const AuthenticationForm = (props: AuthenticationFormProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    kind = 'default',
    className,
    onSuccess,
  } = props;

  // ##########################################################################
  // STORES
  // #########################################################################

  const setUser = useUsers(AUTH, s => s.setUser);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [isSignInOpen, setSignInOpen] = useState(true);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const toggleSignIn = () => setSignInOpen(!isSignInOpen);

  const handleSuccess = async (authResponse: AuthenticatedResponse) => {
    const {
      user,
      token,
    } = authResponse;

    setUser(user);
    posthog.identify(user);
    extension.setAuthentication(user, token);

    if (
      onSuccess
    ) onSuccess(user);

    reload();
  };

  // ##########################################################################
  // COMPUTED STYLES
  // #########################################################################

  const authenticationFormClassName = classNames({
    [styles.authenticationForm]: true,
    [styles.flat]: kind === 'flat',
    [className]: className,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={authenticationFormClassName}>
      <div className={styles.title}>
        {isSignInOpen ? (
          <Text
            kind="heading-3"
            color="neutral-600"
            weight="600"
          >
            Nice to see you&nbsp;again!
          </Text>
        ) : (
          <Text
            kind="heading-3"
            color="neutral-600"
            weight="600"
          >
            Sign up to start&nbsp;saving
          </Text>
        )}
      </div>

      <div className={styles.providers}>
        <GoogleButton onSuccess={handleSuccess} />
      </div>

      <div className={styles.divider}>
        <div className={styles.dividerText}>
          <Text
            kind="caption"
            size="small"
            color="neutral-500"
          >
            OR
          </Text>
        </div>
      </div>

      <div className={styles.form}>
        {isSignInOpen ? (
          <SignIn
            kind={kind}
            onSuccess={handleSuccess}
          />
        ) : (
          <SignUp
            kind={kind}
            onSuccess={handleSuccess}
            navigateToSignIn={toggleSignIn}
          />
        )}
      </div>

      <div className={styles.links}>
        <Text
          kind="paragraph"
          size="regular"
        >
          {isSignInOpen ? (
            'Don’t have an account?'
          ) : (
            'Already have an account?'
          )}
        </Text>

        <div className={styles.link}>
          <Button
            kind="link"
            color="primary"
            onClick={toggleSignIn}
          >
            <Text
              kind="paragraph"
              size="regular"
              color="primary-600"
              weight="500"
            >
              {isSignInOpen ? (
                'Sign Up'
              ) : (
                'Sign In'
              )}
            </Text>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationForm;
